import React from 'react'
import {Box, Chip, Typography, Paper} from '@mui/material'
import {ArrowForward as ArrowForwardIcon} from '@mui/icons-material'
import {Link} from 'react-router-dom'
import {navigationConfig} from '../../config/routeStatus'
import type {IntentResult} from './types'
import {usePostHog} from '../../contexts/PostHogProvider'
import {useOptionalUserIdentity} from '../../contexts/UserIdentityContext'

// Chat agents have no dedicated page — handled inline in chat
const CHAT_AGENT_IDS = new Set(['discovery_interviewer', 'website_builder', 'rfp_builder'])

function resolveToolInfo(intent: IntentResult): {name: string; url: string} | null {
  if (intent.intent === 'community_apps' && intent.externalUrl) {
    return {name: 'Community App', url: intent.externalUrl}
  }

  if (CHAT_AGENT_IDS.has(intent.intent)) return null

  const toolId = intent.suggestedToolId ?? intent.intent

  // Try exact toolId match first, then underscore→hyphen variant, then prefix match
  // This handles tab_plan → tab-plan-generator, interview_notetaker → note-taking, etc.
  const entry = Object.entries(navigationConfig).find(([, cfg]) => {
    const hyphenated = toolId.replace(/_/g, '-')
    return (
      cfg.toolId === toolId ||
      cfg.toolId === hyphenated ||
      cfg.toolId.startsWith(hyphenated) ||
      hyphenated.startsWith(cfg.toolId)
    )
  })

  if (!entry) return null
  const [path, cfg] = entry
  return {name: cfg.displayName, url: path}
}

interface ToolSuggestionCardProps {
  intent: IntentResult
}

const ToolSuggestionCard: React.FC<ToolSuggestionCardProps> = ({intent}) => {
  const {trackEvent} = usePostHog()
  // Optional: this shared card also renders in the standalone Ros app, which has no
  // Studio UserIdentityProvider. department is only a tracking attribute, so null is fine.
  const department = useOptionalUserIdentity()?.department ?? null

  if (intent.intent === 'discovery_interviewer' || intent.intent === 'general_question') {
    return null
  }

  const toolInfo = resolveToolInfo(intent)
  if (!toolInfo) return null

  const coveragePct = Math.round(intent.confidence * 100)
  const isPartialMatch = coveragePct < 80
  const isExternal = intent.intent === 'community_apps'

  const handleClick = () => {
    trackEvent('r3_chat_tool_navigation_clicked', {
      tool_name: toolInfo.name,
      tool_url: toolInfo.url,
      intent: intent.intent,
      confidence: intent.confidence,
      user_department: department,
    })
  }

  const cardContent = (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5,
        borderColor: 'primary.light',
        borderRadius: 2,
        overflow: 'hidden',
        textDecoration: 'none',
        display: 'block',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              Open {toolInfo.name}
            </Typography>
            {isPartialMatch && (
              <Chip
                label={`~${coveragePct}% match`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{height: 18, fontSize: '0.65rem'}}
              />
            )}
          </Box>
          {isPartialMatch && (
            <Typography variant="caption" color="text.secondary">
              Partial match — some requirements may not be covered
            </Typography>
          )}
        </Box>
        <ArrowForwardIcon fontSize="small" sx={{color: 'primary.main', flexShrink: 0}} />
      </Box>
    </Paper>
  )

  if (isExternal) {
    return (
      <a href={toolInfo.url} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}} onClick={handleClick}>
        {cardContent}
      </a>
    )
  }

  return (
    <Link to={toolInfo.url} style={{textDecoration: 'none'}} onClick={handleClick}>
      {cardContent}
    </Link>
  )
}

export default ToolSuggestionCard
